import {
  API_PARTNER_PORTAL_FORM_OPTIONS,
  API_PARTNER_PORTAL_MY_PROJECTS,
  API_PARTNER_PORTAL_PROJECT_DETAIL,
  API_PARTNER_PORTAL_PAE_PROJECT_DETAIL,
  API_PARTNER_PORTAL_PAE_PROJECT_ACTIONS,
  API_PARTNER_PORTAL_LEADERSHIP_PIPELINE,
  API_PARTNER_PORTAL_LEADERSHIP_PIPELINE_PROJECTS,
  API_PARTNER_PORTAL_PARTNER_COMPANIES,
  API_PARTNER_PORTAL_PARTNER_COMPANIES_FORM_OPTIONS,
  API_PARTNER_PORTAL_PARTNER_COMPANY_DETAIL,
  API_PARTNER_PORTAL_PAE_PROJECT_QUEUE,
  API_PARTNER_PORTAL_SUBMIT,
  API_PARTNER_PORTAL_PROJECT_MASTER_DATA,
  API_PARTNER_PORTAL_PROJECT_MASTER_DATA_ITEMS,
} from '../../../../Redux/Constants/apiUrls';
import { PartnerApiError, normalizePartnerFieldErrors } from '../Utils/partnerApiFeedback';

const extractErrorMessage = (errorBody, errorText = '') => {
  if (Array.isArray(errorBody)) {
    const arrayMessage = errorBody
      .map((item) => item?.error || item?.message || item?.msg)
      .find(Boolean);
    return arrayMessage || errorText || 'Something went wrong';
  }

  if (typeof errorBody === 'string') {
    return errorBody || errorText || 'Something went wrong';
  }

  if (errorBody && typeof errorBody === 'object') {
    if (typeof errorBody.detail === 'string') {
      return errorBody.detail;
    }

    return (
      errorBody.error ||
      errorBody.message ||
      errorBody.msg ||
      errorText ||
      'Something went wrong'
    );
  }

  return errorText || 'Something went wrong';
};

const buildApiError = (errorBody, errorText, status) => {
  const fieldErrors = normalizePartnerFieldErrors(errorBody?.errors);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const message = hasFieldErrors
    ? Object.values(fieldErrors)[0]
    : extractErrorMessage(errorBody, errorText);

  return new PartnerApiError(message, {
    fieldErrors,
    status,
  });
};

const handleErrors = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      return buildApiError(errorJson, errorText, response.status);
    } catch (e) {
      return buildApiError(errorText, errorText, response.status);
    }
  }
  return null;
};

const partnerPortalRequest = async (accessToken, url, options = {}) => {
  if (!accessToken) {
    throw new PartnerApiError('Your session has expired. Please sign in again.');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
  });

  const apiError = await handleErrors(response);
  if (apiError) {
    throw apiError;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const partnerPortalApi = {
  fetchFormOptions(accessToken) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_FORM_OPTIONS);
  },

  submitProject(accessToken, payload) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_SUBMIT, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  fetchMyProjects(accessToken) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_MY_PROJECTS);
  },

  fetchPAEProjectQueue(accessToken) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PAE_PROJECT_QUEUE);
  },

  fetchPAEProjectById(accessToken, projectId) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PAE_PROJECT_DETAIL(projectId));
  },

  fetchProjectById(accessToken, projectId) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PROJECT_DETAIL(projectId));
  },

  patchProject(accessToken, projectId, payload) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PROJECT_DETAIL(projectId), {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deleteProject(accessToken, projectId) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PROJECT_DETAIL(projectId), {
      method: 'DELETE',
    });
  },

  postPAEProjectAction(accessToken, projectId, payload) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PAE_PROJECT_ACTIONS(projectId), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  fetchPartnerCompanies(accessToken) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PARTNER_COMPANIES);
  },

  fetchPartnerCompanyFormOptions(accessToken) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PARTNER_COMPANIES_FORM_OPTIONS);
  },

  createPartnerCompany(accessToken, payload) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PARTNER_COMPANIES, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  patchPartnerCompany(accessToken, companyId, payload) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PARTNER_COMPANY_DETAIL(companyId), {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  fetchLeadershipPipeline(accessToken) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_LEADERSHIP_PIPELINE);
  },

  fetchLeadershipPipelineProjects(accessToken, { dimension, value, status } = {}) {
    const params = new URLSearchParams({
      dimension,
      value,
    });
    if (status) {
      params.set('status', status);
    }
    const url = `${API_PARTNER_PORTAL_LEADERSHIP_PIPELINE_PROJECTS}?${params.toString()}`;
    return partnerPortalRequest(accessToken, url);
  },

  fetchProjectMasterData(accessToken) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PROJECT_MASTER_DATA);
  },

  addProjectMasterDataItem(accessToken, payload) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PROJECT_MASTER_DATA_ITEMS, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  patchProjectMasterDataItem(accessToken, payload) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PROJECT_MASTER_DATA_ITEMS, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  deleteProjectMasterDataItem(accessToken, payload) {
    return partnerPortalRequest(accessToken, API_PARTNER_PORTAL_PROJECT_MASTER_DATA_ITEMS, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
  },
};
